/**
 * Privacy Policy — GDPR-compliant template per PRD §7.5.
 * Public route (/privacy), accessible without auth.
 */
import { Link } from 'react-router-dom';
import { routes } from '../../routes.js';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-canvas font-sans">
      <header className="border-b border-scope-border-soft bg-white px-8 py-5">
        <div className="mx-auto max-w-3xl flex items-center justify-between">
          <Link to={routes.login} className="text-lg font-bold tracking-tight text-scope-navy">
            Scope
          </Link>
          <Link
            to={routes.login}
            className="text-sm text-scope-text-muted hover:text-scope-navy transition"
          >
            ← Back to login
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-scope-navy">Privacy Policy</h1>
          <p className="mt-2 text-sm text-scope-text-muted">
            Last updated: May 2026 · Effective date: May 2026
          </p>
        </div>

        <div className="space-y-8 text-sm leading-relaxed">
          <Section title="1. Who we are">
            <p>
              Scope is operated by GestureLoop Ltd. We provide agency project and client management
              software at <span className="font-medium">scope.gestureloop.com</span>. For GDPR
              purposes, GestureLoop Ltd is the <strong>data controller</strong>.
            </p>
            <p className="mt-3">
              Contact:{' '}
              <a
                href="mailto:privacy@scope.gestureloop.com"
                className="text-scope-navy underline hover:no-underline"
              >
                privacy@scope.gestureloop.com
              </a>
            </p>
          </Section>

          <Section title="2. What data we collect">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Account data:</strong> name, email address, role, agency affiliation.
              </li>
              <li>
                <strong>Work data:</strong> engagements, projects, phases, milestones, tasks,
                deliverables, documents, reports, and check-in logs entered into the platform.
              </li>
              <li>
                <strong>Activity data:</strong> login timestamps, terms-of-service acceptance
                timestamps, notification read status.
              </li>
              <li>
                <strong>Session data:</strong> authentication tokens (via Supabase Auth). We use no
                marketing cookies or third-party tracking cookies.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use your data">
            <ul className="list-disc space-y-2 pl-5">
              <li>To provide and maintain the Scope platform.</li>
              <li>To authenticate you and manage your session securely.</li>
              <li>
                To send transactional emails (invitations, approval notifications) via Resend.
              </li>
              <li>To surface workflow prompts and reminders relevant to your engagements.</li>
              <li>To maintain an audit trail per GDPR Article 5(1)(f) integrity requirements.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> use your data for advertising, sell it to third parties, or
              use it for automated profiling or decision-making.
            </p>
          </Section>

          <Section title="4. Where your data is stored">
            <p>
              All data is stored <strong>within the European Union</strong>:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>
                <strong>Database &amp; Auth:</strong> Supabase (Frankfurt, Germany — AWS
                eu-central-1). Supabase Inc. acts as a <strong>data processor</strong> under a Data
                Processing Agreement (DPA) available on request.
              </li>
              <li>
                <strong>Transactional email:</strong> Resend Inc., used only to deliver
                notifications. Resend processes minimal data as a data processor.
              </li>
              <li>
                <strong>Application servers:</strong> Hetzner Online GmbH (Frankfurt or Helsinki —
                EU).
              </li>
            </ul>
            <p className="mt-3">
              All timestamps are stored in UTC and converted to your agency timezone for display.
            </p>
          </Section>

          <Section title="5. Data retention">
            <p>
              We retain your data for as long as your account or engagement remains active, unless
              you exercise your right to erasure (see §6).
            </p>
            <p className="mt-3">After account deletion or client erasure:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Personal data (name, email) is deleted immediately.</li>
              <li>
                Audit name-snapshots are retained for integrity purposes only — they contain only a
                display name, not contact details.
              </li>
            </ul>
          </Section>

          <Section title="6. Your rights (GDPR Articles 15–22)">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Right of access (Art. 15):</strong> Request a copy of all personal data we
                hold about you.
              </li>
              <li>
                <strong>Right to rectification (Art. 16):</strong> Correct inaccurate data via your
                profile settings or by contacting us.
              </li>
              <li>
                <strong>Right to erasure (Art. 17):</strong> Request deletion of your account or a
                specific client record. Deletion cascades to all associated engagements, projects,
                deliverables, documents, and reports. Agency admins can trigger client erasure
                directly from the Scope UI.
              </li>
              <li>
                <strong>Right to data portability (Art. 20):</strong> Request an export of your data
                in JSON format. Contact us at the address below.
              </li>
              <li>
                <strong>Right to object (Art. 21):</strong> Object to processing where our lawful
                basis is legitimate interests.
              </li>
              <li>
                <strong>Right to restrict processing (Art. 18):</strong> Ask us to pause processing
                while a dispute is resolved.
              </li>
            </ul>
            <p className="mt-4">
              To exercise any right, email{' '}
              <a
                href="mailto:privacy@scope.gestureloop.com"
                className="text-scope-navy underline hover:no-underline"
              >
                privacy@scope.gestureloop.com
              </a>
              . We will respond within 30 days.
            </p>
          </Section>

          <Section title="7. Lawful basis for processing">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Contract (Art. 6(1)(b)):</strong> Processing necessary to deliver Scope
                under our terms of service.
              </li>
              <li>
                <strong>Legitimate interests (Art. 6(1)(f)):</strong> Security, fraud prevention,
                audit trail integrity, and service improvement.
              </li>
              <li>
                <strong>Consent (Art. 6(1)(a)):</strong> Where you have explicitly accepted these
                terms at first login.
              </li>
            </ul>
          </Section>

          <Section title="8. Cookies">
            <p>
              Scope uses <strong>only one type of cookie</strong>: an authentication session cookie
              set by Supabase Auth after you log in. This is strictly necessary for the platform to
              function and is not used for tracking or advertising.
            </p>
            <p className="mt-3">
              We do not use Google Analytics, Facebook Pixel, or any third-party tracking script.
            </p>
          </Section>

          <Section title="9. Data Processing Agreement">
            <p>
              For agency customers who process personal data of their contacts through Scope,
              GestureLoop Ltd acts as a <strong>data processor</strong> on your behalf (GDPR Art.
              28). A DPA is available on request at{' '}
              <a
                href="mailto:privacy@scope.gestureloop.com"
                className="text-scope-navy underline hover:no-underline"
              >
                privacy@scope.gestureloop.com
              </a>
              .
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We will notify users via email and in-app notification of any material changes at
              least 14 days before they take effect.
            </p>
          </Section>

          <Section title="11. Complaints">
            <p>
              You have the right to lodge a complaint with your local supervisory authority. In
              Ireland: <strong>Data Protection Commission (DPC)</strong> at{' '}
              <a
                href="https://www.dataprotection.ie"
                target="_blank"
                rel="noopener noreferrer"
                className="text-scope-navy underline hover:no-underline"
              >
                dataprotection.ie
              </a>
              .
            </p>
          </Section>
        </div>

        <div className="mt-12 flex items-center justify-between border-t border-scope-border-soft pt-6 text-sm text-scope-text-muted">
          <span>GestureLoop Ltd &mdash; Scope v1</span>
          <Link to={routes.terms} className="hover:text-scope-navy">
            Terms of Service →
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-base font-semibold text-scope-navy">{title}</h2>
      <div className="text-scope-text-muted">{children}</div>
    </section>
  );
}
